import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MapPin, Star, IndianRupee, MessageSquare, Gavel, Check, Wifi, Coffee, Car,
  Sparkles, ShieldCheck, Clock, Ban, Tag, Percent, X, ChevronLeft, ChevronRight, CreditCard, Smartphone, Landmark, Layers, Wallet, Calendar, Pencil, Zap, AlertCircle, ThumbsUp,
} from "lucide-react";

const AMENITY_ICONS = { "Free WiFi": Wifi, "Restaurant": Coffee, "Free Parking": Car };
const TAX_RATE = 0.12; // GST 12% for room tariff <= ₹7500, single-rate for MVP display
const SERVICE_FEE = 99;

// Build a Google Maps embed src from a variety of user-provided formats
function toMapsEmbed(mapUrl, address) {
  const fallbackQ = encodeURIComponent(address || "");
  if (!mapUrl) return `https://www.google.com/maps?q=${fallbackQ}&output=embed`;
  const s = String(mapUrl).trim();
  // Raw lat,lng
  if (/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(s)) {
    return `https://www.google.com/maps?q=${encodeURIComponent(s)}&output=embed`;
  }
  // Already an embed URL — use as-is
  if (s.includes("google.com/maps/embed")) return s;
  // Try to extract q= parameter from a maps URL and re-embed cleanly
  try {
    const u = new URL(s);
    const q = u.searchParams.get("q") || u.searchParams.get("query");
    if (q) return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
    // No q param, but a maps URL — append output=embed
    if (u.hostname.includes("google") && u.pathname.includes("/maps")) {
      const sep = s.includes("?") ? "&" : "?";
      return `${s}${sep}output=embed`;
    }
  } catch { /* not a URL */ }
  // Treat as plain place/address string
  return `https://www.google.com/maps?q=${encodeURIComponent(s)}&output=embed`;
}

function StarRow({ value, size = "w-4 h-4", onSelect }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect && onSelect(n)}
          className={onSelect ? "cursor-pointer" : "cursor-default"}
          data-testid={onSelect ? `star-input-${n}` : undefined}
        >
          <Star className={`${size} ${n <= value ? "fill-amber-500 stroke-amber-500" : "stroke-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function HotelDetail() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reviews
  const [reviews, setReviews] = useState({ items: [], count: 0, average: 0 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rvRating, setRvRating] = useState(5);
  const [rvTitle, setRvTitle] = useState("");
  const [rvComment, setRvComment] = useState("");

  // Negotiate + shared date state (pre-filled from search page URL if present)
  const [negOpen, setNegOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [offerPrice, setOfferPrice] = useState(0);
  const [checkin, setCheckin] = useState(searchParams.get("checkin") || "");
  const [checkout, setCheckout] = useState(searchParams.get("checkout") || "");
  const [roomsNeeded, setRoomsNeeded] = useState(1);
  // AI Negotiation Engine — instant quote state (Iter28)
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null);   // {decision, counter_price, message, min_allowed, listed_price, negotiation_id?}
  const [booking, setBooking] = useState(false);

  // Iter29 — per-room availability keyed by room_id → { minAvail, sold_out }
  // for the current [checkin, checkout] window. Refetched on room/date change.
  const [avail, setAvail] = useState({}); // { [room_id]: { min_avail, sold_out, loading } }

  // Iter31 — AI Virtual Room Experience™ modal
  const [virtualRoomOpen, setVirtualRoomOpen] = useState(false);

  // Booking
  const [bookOpen, setBookOpen] = useState(false);
  const [bookRoom, setBookRoom] = useState(null);
  const [upiTxn, setUpiTxn] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // { code, discount }
  const [suggestedCoupon, setSuggestedCoupon] = useState(null);
  const [applying, setApplying] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [editDates, setEditDates] = useState(false);
  // Booking-for: 'self' (default) or 'other'. When 'other' we collect guest
  // name + phone so the hotel front-desk knows who's actually checking in.
  const [bookingFor, setBookingFor] = useState("self");
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  // Editable contact for THIS booking — pre-filled from the logged-in user
  // but the traveller can update them here (e.g. use a different phone the
  // WhatsApp invoice should land on).
  const [bookerEmail, setBookerEmail] = useState("");
  const [bookerPhone, setBookerPhone] = useState("");
  useEffect(() => {
    if (bookOpen && user) {
      setBookerEmail((v) => v || user.email || "");
      setBookerPhone((v) => v || user.phone || "");
    }
  }, [bookOpen, user]);

  useEffect(() => {
    api.get(`/hotels/${hotelId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
    api.get(`/hotels/${hotelId}/reviews`).then(({ data }) => setReviews(data)).catch((e) => console.debug("fetch failed:", e?.message));
    // Cache rule preset labels so we can render hotel.rules_preset[] as human text
    if (!window.__HOTEL_RULE_PRESETS__) {
      api.get("/hotel-rule-presets").then(({ data }) => { window.__HOTEL_RULE_PRESETS__ = data.presets || []; }).catch((e) => console.debug("fetch failed:", e?.message));
    }
  }, [hotelId]);

  // Fetch wallet balance for logged-in traveller
  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/wallet").then(({ data }) => setWalletBalance(data.balance || 0)).catch((e) => console.debug("fetch failed:", e?.message));
    }
  }, [user]);

  // Suggest best coupon whenever book dialog opens on a room
  useEffect(() => {
    if (!bookOpen || !bookRoom) { setSuggestedCoupon(null); return; }
    api.get(`/coupons/best`, { params: { amount: bookRoom.price } })
      .then(({ data }) => setSuggestedCoupon(data))
      .catch(() => setSuggestedCoupon(null));
  }, [bookOpen, bookRoom]);

  // Iter29 — refresh per-room availability whenever hotel loads or dates change.
  // Rooms without a stay window fall back to the room's static inventory field.
  useEffect(() => {
    const rooms = data?.rooms || [];
    if (!rooms.length || !checkin || !checkout) { setAvail({}); return; }
    let cancelled = false;
    const params = { date_from: checkin, date_to: checkout };
    (async () => {
      const next = {};
      await Promise.all(rooms.map(async (r) => {
        try {
          const { data: a } = await api.get(`/rooms/${r.room_id}/availability`, { params });
          const min_avail = Math.min(...(a.days || []).map((d) => d.available));
          next[r.room_id] = { min_avail: Number.isFinite(min_avail) ? min_avail : 0, sold_out: min_avail <= 0, loading: false };
        } catch (e) {
          next[r.room_id] = { min_avail: r.inventory || 0, sold_out: (r.inventory || 0) <= 0, loading: false };
        }
      }));
      if (!cancelled) setAvail(next);
    })();
    return () => { cancelled = true; };
  }, [data, checkin, checkout]);

  const priceBreakup = useMemo(() => {
    if (!bookRoom) return null;
    const base = bookRoom.price;
    const discount = couponApplied?.discount || 0;
    const afterDiscount = Math.max(0, base - discount);
    const taxes = Math.round(afterDiscount * TAX_RATE);
    const grand = afterDiscount + taxes + SERVICE_FEE;
    const walletUsed = useWallet ? Math.min(walletBalance, grand) : 0;
    const total = grand - walletUsed;
    return { base, discount, taxes, service: SERVICE_FEE, walletUsed, grand, total };
  }, [bookRoom, couponApplied, useWallet, walletBalance]);

  const hotel = data?.hotel;
  const rooms = data?.rooms || [];
  const hotelJsonLd = hotel ? {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "name": hotel.name,
    "description": hotel.description,
    "image": hotel.photos || [],
    "address": { "@type": "PostalAddress", "streetAddress": hotel.address, "addressLocality": hotel.city, "addressCountry": "IN" },
    "priceRange": rooms.length ? `₹${Math.min(...rooms.map((r) => r.price)).toLocaleString("en-IN")} - ₹${Math.max(...rooms.map((r) => r.price)).toLocaleString("en-IN")}` : undefined,
    "aggregateRating": reviews.count > 0 ? { "@type": "AggregateRating", "ratingValue": reviews.average, "reviewCount": reviews.count } : undefined,
    "amenityFeature": (hotel.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", "name": a })),
  } : null;
  useSEO({
    title: hotel ? `${hotel.name}, ${hotel.city}` : "Hotel details",
    description: hotel ? `Book ${hotel.name} in ${hotel.city} on SwaanStay. ${hotel.description?.slice(0, 160) || "Verified reviews, negotiate the price and save on your stay."}` : undefined,
    image: hotel?.photos?.[0],
    keywords: hotel ? `${hotel.name}, ${hotel.city} hotels, ${hotel.category || 'boutique'} hotel ${hotel.city}` : undefined,
    url: hotel ? `https://price-compete.emergent.host/hotels/${hotel.slug || hotel.hotel_id}` : undefined,
    jsonLd: hotelJsonLd,
  });

  if (loading) return <div className="p-10"><span className="hw-pulse-dot" /></div>;
  if (!hotel) return <div className="p-10">Hotel not found.</div>;

  const mapSrc = toMapsEmbed(hotel.map_url, `${hotel.name}, ${hotel.address || hotel.city}`);

  const openBooking = (r) => {
    setBookRoom(r);
    setCouponCode("");
    setCouponApplied(null);
    setUpiTxn("");
    setPaymentMethod("upi");
    setUseWallet(false);
    setEditDates(!checkin || !checkout); // auto-open date editor if not set
    setBookOpen(true);
  };

  const applyCoupon = async (code) => {
    if (!bookRoom) return;
    const c = (code || couponCode || "").trim().toUpperCase();
    if (!c) return toast.error("Enter a coupon code");
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: c, amount: bookRoom.price });
      setCouponApplied({ code: data.code, discount: data.discount });
      setCouponCode(data.code);
      toast.success(`Coupon ${data.code} applied — you saved ₹${data.discount.toLocaleString("en-IN")}`);
    } catch (e) {
      setCouponApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplying(false);
    }
  };

  const submitNegotiation = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "customer") return toast.error("Only travellers can negotiate");
    if (!selectedRoom || !offerPrice) return toast.error("Pick a room and enter your offer");
    // Iter28 — AI auto-negotiate rooms bypass the manual queue via the Slider UI.
    // This function is retained as the fallback for `manual_review` / rejected cases.
    try {
      await api.post("/negotiations", {
        hotel_id: hotelId,
        room_id: selectedRoom.room_id,
        initial_price: selectedRoom.price,
        offer_price: Number(offerPrice),
        checkin, checkout,
        rooms_needed: Number(roomsNeeded),
      });
      toast.success("Offer sent! Hotel will respond shortly.");
      setNegOpen(false);
      navigate("/negotiations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send offer");
    }
  };

  // Iter28 — call the AI Negotiation Engine for an instant quote
  const getInstantQuote = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "customer") return toast.error("Only travellers can negotiate");
    if (!selectedRoom || !offerPrice) return toast.error("Move the slider to pick your offer");
    setQuoting(true);
    try {
      const { data } = await api.post("/negotiations/instant-quote", {
        room_id: selectedRoom.room_id,
        offer_price: Number(offerPrice),
        group_size: Number(roomsNeeded) || 1,
        checkin: checkin || undefined,
        checkout: checkout || undefined,
        rooms_needed: Number(roomsNeeded) || 1,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch quote");
    } finally {
      setQuoting(false);
    }
  };

  // Iter28 — after AI accepts, commit the negotiation and jump into booking flow
  const acceptQuoteAndBook = async (priceToBook) => {
    if (!user || !selectedRoom) return;
    setBooking(true);
    try {
      const { data } = await api.post("/negotiations/instant-quote", {
        room_id: selectedRoom.room_id,
        offer_price: Number(priceToBook),
        group_size: Number(roomsNeeded) || 1,
        checkin: checkin || undefined,
        checkout: checkout || undefined,
        rooms_needed: Number(roomsNeeded) || 1,
        commit: true,
      });
      if (data.decision !== "accept" || !data.negotiation_id) {
        toast.error("Could not lock in that price — please try again.");
        setQuote(data);
        return;
      }
      // Create the pending-payment booking at the negotiated price
      const { data: bkg } = await api.post(`/negotiations/${data.negotiation_id}/book`, {
        rooms: Number(roomsNeeded) || 1,
        checkin: checkin || undefined,
        checkout: checkout || undefined,
      });
      toast.success(`Locked in ₹${Number(priceToBook).toLocaleString("en-IN")} — complete payment to confirm.`);
      setNegOpen(false);
      setQuote(null);
      navigate(`/pay/booking/${bkg.booking_id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || "Just sold — please refresh and try again.");
        setNegOpen(false);
        setQuote(null);
        try {
          const { data: fresh } = await api.get(`/hotels/${hotelId}`);
          setData(fresh);
        } catch { /* ignore */ }
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not book at that price");
    } finally {
      setBooking(false);
    }
  };

  const startChat = async () => {
    if (!user) return navigate("/login");
    try {
      const { data: thread } = await api.post("/chat/start", { hotel_id: hotelId });
      navigate(`/chats/${thread.thread_id}`);
    } catch { toast.error("Chat error"); }
  };

  const submitBooking = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "customer") return toast.error("Only travellers can book");
    if (!bookRoom) return;
    // Contact validation — email is mandatory, phone must be present, and if
    // booking for someone else we also need the guest's name + phone.
    if (!bookerEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(bookerEmail)) {
      return toast.error("Please enter a valid email — the invoice will be sent here.");
    }
    if (!bookerPhone || bookerPhone.replace(/\D/g, "").length < 10) {
      return toast.error("Please enter a valid phone number for booking updates.");
    }
    if (bookingFor === "other") {
      if (!guestName.trim()) return toast.error("Guest name is required when booking for someone else.");
      if (!guestPhone || guestPhone.replace(/\D/g, "").length < 10) {
        return toast.error("Guest phone is required when booking for someone else.");
      }
    }
    if (paymentMethod !== "upi" && paymentMethod !== "wallet_only" && priceBreakup.total > 0) {
      return toast.error("This payment method is activating soon. Please use UPI for now.");
    }
    // Iter44 — Razorpay flow. Direct bookings with a cash portion are created as
    // pending_payment on the server; we then navigate to the payment page which
    // opens Razorpay Checkout (which auto-generates a fresh UPI QR per txn).
    const useRazorpay = priceBreakup.total > 0;
    try {
      // SEC-002: server recomputes price. Client only sends room_id, dates, rooms,
      // coupon code, wallet_used AND (new) guest/contact details.
      const { data: booking } = await api.post("/bookings", {
        hotel_id: hotelId,
        room_id: bookRoom.room_id,
        rooms: 1, checkin, checkout,
        source: "direct",
        coupon_code: couponApplied?.code,
        payment_method: useRazorpay ? "razorpay" : paymentMethod,
        defer_payment: useRazorpay,
        wallet_used: useRazorpay ? 0 : priceBreakup.walletUsed,
        booking_for: bookingFor,
        guest_name: bookingFor === "other" ? guestName.trim() : undefined,
        guest_phone: bookingFor === "other" ? guestPhone.trim() : undefined,
        contact_email: bookerEmail.trim(),
        contact_phone: bookerPhone.trim(),
      });
      setBookOpen(false);
      if (useRazorpay && booking?.booking_id) {
        navigate(`/pay/booking/${booking.booking_id}`);
        return;
      }
      // Fully wallet-covered → already confirmed on the server.
      toast.success("Booking confirmed! Invoice + confirmation sent to your email & WhatsApp.");
      if (booking?.booking_id) {
        navigate(`/invoice/${booking.booking_id}`);
      } else {
        navigate("/bookings");
      }
    } catch (e) {
      // Iter29 — 409 = inventory just sold out. Soft-refresh availability and
      // guide the traveller to pick a different room or date.
      if (e?.response?.status === 409) {
        toast.error(e?.response?.data?.detail || "Just sold — please pick another room or dates.");
        setBookOpen(false);
        try {
          const { data: fresh } = await api.get(`/hotels/${hotelId}`);
          setData(fresh);
        } catch { /* ignore */ }
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not book");
    }
  };

  const submitReview = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "customer") return toast.error("Only travellers can post reviews");
    if (!rvComment.trim()) return toast.error("Please add a short comment");
    try {
      await api.post("/reviews", {
        hotel_id: hotelId, rating: rvRating, title: rvTitle, comment: rvComment,
      });
      toast.success("Thanks — your review is live!");
      setReviewOpen(false);
      setRvTitle(""); setRvComment(""); setRvRating(5);
      const { data } = await api.get(`/hotels/${hotelId}/reviews`);
      setReviews(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit review");
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="hw-card overflow-hidden">
              <div className="aspect-[16/10] bg-slate-100 relative">
                <img src={hotel.photos?.[0]} alt={hotel.name} className="w-full h-full object-cover" />
                {/* Iter31 — AI Virtual Room Experience™ CTA (shows when panorama or guided tour is ready) */}
                {hotel.virtual_room?.status && (
                  <button
                    onClick={() => setVirtualRoomOpen(true)}
                    data-testid="virtual-room-cta"
                    className="absolute bottom-4 right-4 bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white px-5 py-3 rounded-sm font-bold shadow-lg flex items-center gap-2 group"
                  >
                    <Sparkles className="w-4 h-4 group-hover:animate-pulse" />
                    <span className="text-sm">AI Virtual Room Experience™</span>
                  </button>
                )}
              </div>
              {hotel.photos?.length > 1 && (
                <div className="grid grid-cols-3 gap-1 p-1 bg-white">
                  {hotel.photos.slice(1, 4).map((p, i) => (
                    <img key={i} src={p} alt="" className="aspect-[4/3] object-cover" />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_20px_60px_-24px_rgba(200,163,77,0.35)] p-6 sticky top-20">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FBF3DA] border border-[#C8A34D]/40 text-[#A6852F] text-xs font-bold" data-testid="hotel-rating-badge">
                  <Star className="w-3 h-3 fill-[#C8A34D] stroke-[#C8A34D]" /> {(reviews.average || hotel.rating || 0).toFixed(1)}
                </div>
                {reviews.count > 0 && (
                  <span className="text-xs text-slate-500 font-semibold" data-testid="reviews-count-inline">({reviews.count} review{reviews.count > 1 ? "s" : ""})</span>
                )}
                {hotel.category && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-xs font-semibold capitalize">{hotel.category}</span>}
              </div>
              <h1 className="font-head text-3xl md:text-4xl font-black mt-3 text-slate-900">{hotel.name}</h1>
              <div className="flex items-center gap-1 text-slate-500 mt-2 font-body"><MapPin className="w-4 h-4" /> {hotel.address || hotel.city}</div>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed font-body">{hotel.description}</p>

              <div className="my-5 h-px bg-slate-200" />

              <div className="ss-label text-[#A6852F]">Amenities</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {hotel.amenities?.map((a) => {
                  const I = AMENITY_ICONS[a] || Sparkles;
                  return <span key={a} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-slate-200 bg-white text-slate-700 text-xs font-semibold"><I className="w-3 h-3" /> {a}</span>;
                })}
              </div>

              <Button onClick={startChat} variant="outline" className="w-full mt-5 ss-btn ss-btn-ghost border-[#C8A34D] text-[#A6852F] hover:bg-[#0F1115] hover:text-[#E8D8A8] hover:border-[#0F1115]" data-testid="chat-hotel-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Chat with hotel
              </Button>
            </div>
          </div>
        </div>

        {/* Rooms */}
        <div className="mt-10">
          <div className="ss-label text-[#A6852F]">Rooms</div>
          <h2 className="font-head text-3xl font-black mt-2 text-slate-900">Choose a room — then <span className="ss-underline-gold text-[#A6852F]">negotiate</span></h2>

          <div className="grid gap-4 mt-5" data-testid="rooms-list">
            {rooms.map((r) => {
              const liveAvail = avail[r.room_id];
              const hasWindow = !!(checkin && checkout);
              const minAvail = liveAvail ? liveAvail.min_avail : (r.inventory || 0);
              const soldOut = r.is_sold_out || (hasWindow ? liveAvail?.sold_out : (r.inventory || 0) <= 0);
              return (
              <div key={r.room_id} className="rounded-2xl bg-white border border-slate-200 hover:border-[#C8A34D] hover:shadow-[0_20px_50px_-24px_rgba(200,163,77,0.35)] transition-all p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`room-${r.room_id}`}>
                <div className="md:col-span-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-head font-bold text-xl text-slate-900">{r.room_type}</h3>
                    {r.breakfast_included && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#E7F7EF] text-[#0F5934] border border-[#16A34A]/30 text-xs font-semibold">☕ Breakfast included</span>}
                    {soldOut && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs font-semibold" data-testid={`sold-out-${r.room_id}`}>Sold out</span>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 font-body">{r.description}</p>
                  <div className="ss-label text-slate-500 mt-2" data-testid={`availability-${r.room_id}`}>
                    {hasWindow
                      ? (soldOut ? "Not available for these dates" : `${minAvail} left for your dates`)
                      : `${r.inventory || 0} available`}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="ss-label text-slate-500">Listed</div>
                  <div className="font-head text-2xl font-black flex items-center text-slate-900"><IndianRupee className="w-4 h-4" />{r.price.toLocaleString("en-IN")}</div>
                  <div className="text-[11px] text-slate-500 font-body">+ taxes & fees</div>
                </div>
                <div className="md:col-span-4 flex flex-col gap-2">
                  <Button disabled={soldOut} onClick={() => {
                    setSelectedRoom(r);
                    const floor = Math.max(1, Math.round(r.min_price || r.price * 0.7));
                    setOfferPrice(Math.max(floor, Math.round(r.price * 0.85)));
                    setQuote(null);
                    setNegOpen(true);
                  }} className="ss-btn ss-btn-primary disabled:opacity-50" data-testid={`negotiate-btn-${r.room_id}`}>
                    <Gavel className="w-4 h-4 mr-2" /> {soldOut ? "Sold out" : "Negotiate"}
                  </Button>
                  <Button disabled={soldOut} onClick={() => openBooking(r)} variant="outline" className="ss-btn ss-btn-ghost border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-50" data-testid={`book-btn-${r.room_id}`}>
                    {soldOut ? "Unavailable" : `Book at ₹${r.price.toLocaleString("en-IN")}`}
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Location + Map */}
        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 hw-card overflow-hidden" data-testid="hotel-map">
            <div className="p-5 border-b border-slate-200 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-700" />
              <div className="hw-head font-black text-slate-900">Location</div>
            </div>
            <iframe
              title={`${hotel.name} location`}
              src={mapSrc}
              width="100%" height="360"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="hotel-map-iframe"
            />
          </div>
          <div className="lg:col-span-5 hw-card p-5" data-testid="hotel-nearby">
            <div className="hw-head font-black text-slate-900">Getting around</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {hotel.near_airport && <li>✈️ Airport: <span className="font-semibold">{hotel.near_airport}</span></li>}
              {hotel.near_railway && <li>🚉 Railway: <span className="font-semibold">{hotel.near_railway}</span></li>}
              {hotel.near_bus_stand && <li>🚌 Bus stand: <span className="font-semibold">{hotel.near_bus_stand}</span></li>}
              {(!hotel.near_airport && !hotel.near_railway && !hotel.near_bus_stand) && (
                <li className="text-slate-500">Transport details not provided by hotel yet.</li>
              )}
            </ul>
            {hotel.nearby_attractions?.length > 0 && (
              <>
                <div className="hw-label text-slate-500 mt-5">Nearby attractions</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {hotel.nearby_attractions.map((n) => (
                    <span key={n} className="hw-chip text-slate-700"><MapPin className="w-3 h-3" /> {n}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Policies */}
        <div className="mt-10 hw-card p-6" data-testid="hotel-policies">
          <div className="hw-label text-blue-700">Hotel policies</div>
          <h2 className="hw-head text-2xl font-black text-slate-900 mt-2">What to know before you go</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 grid place-items-center flex-shrink-0"><Clock className="w-5 h-5 text-blue-700" /></div>
              <div>
                <div className="hw-head font-bold text-slate-900">Check-in & Check-out</div>
                <div className="text-sm text-slate-600 mt-1">Check-in from <span className="font-semibold">{hotel.checkin_time || "1:00 PM"}</span> · Check-out by <span className="font-semibold">{hotel.checkout_time || "11:00 AM"}</span></div>
                <div className="text-xs text-slate-500 mt-1">Early check-in / late check-out subject to availability.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-emerald-50 grid place-items-center flex-shrink-0"><ShieldCheck className="w-5 h-5 text-emerald-700" /></div>
              <div>
                <div className="hw-head font-bold text-slate-900">Cancellation</div>
                <div className="text-sm text-slate-600 mt-1">Free cancellation up to <span className="font-semibold">48 hours</span> before check-in. After that, first-night charge applies.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-50 grid place-items-center flex-shrink-0"><Ban className="w-5 h-5 text-amber-700" /></div>
              <div>
                <div className="hw-head font-bold text-slate-900">House rules</div>
                {(hotel.rules_preset?.length > 0 || hotel.rules_custom?.length > 0) ? (
                  <ul className="text-sm text-slate-600 mt-1 space-y-0.5" data-testid="hotel-house-rules">
                    {(hotel.rules_preset || []).map((k) => {
                      const preset = (window.__HOTEL_RULE_PRESETS__ || []).find((p) => p.key === k);
                      return <li key={k}>• {preset?.label || k}</li>;
                    })}
                    {(hotel.rules_custom || []).map((r, i) => <li key={`c-${i}`}>• {r}</li>)}
                  </ul>
                ) : (
                  <ul className="text-sm text-slate-600 mt-1 space-y-0.5">
                    <li>• Valid photo ID required at check-in</li>
                    <li>• No smoking in rooms</li>
                    <li>• Pets on request only</li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-10 hw-card p-6" data-testid="reviews-section">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="hw-label text-blue-700">Guest reviews</div>
              <h2 className="hw-head text-2xl font-black text-slate-900 mt-2 flex items-center gap-3">
                {reviews.average ? reviews.average.toFixed(1) : "—"} / 5
                <StarRow value={Math.round(reviews.average || 0)} />
                <span className="text-sm font-normal text-slate-500" data-testid="reviews-total-count">from {reviews.count} guest{reviews.count === 1 ? "" : "s"}</span>
              </h2>
            </div>
            {user?.role === "customer" && (
              <Button onClick={() => setReviewOpen(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm font-bold" data-testid="write-review-btn">
                Write a review
              </Button>
            )}
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="reviews-list">
            {reviews.items.length === 0 && (
              <div className="text-slate-500 text-sm" data-testid="reviews-empty">Be the first to review this hotel.</div>
            )}
            {reviews.items.map((rv) => (
              <div key={rv.review_id} className="border border-slate-200 rounded-md p-4" data-testid={`review-${rv.review_id}`}>
                <div className="flex items-center justify-between">
                  <div className="hw-head font-bold text-slate-900">{rv.user_name}</div>
                  <StarRow value={rv.rating} size="w-3.5 h-3.5" />
                </div>
                {rv.title && <div className="text-sm font-semibold text-slate-800 mt-1">{rv.title}</div>}
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{rv.comment}</p>
                <div className="text-[11px] text-slate-400 mt-2">{new Date(rv.created_at).toLocaleDateString("en-IN")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Negotiate Dialog — Iter28 AI Slider + Instant Quote */}
      <Dialog open={negOpen} onOpenChange={(v) => { setNegOpen(v); if (!v) setQuote(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="negotiate-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head font-black text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" /> AI Instant Negotiation
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              {selectedRoom?.room_type} at <span className="font-semibold">{hotel.name}</span>
            </DialogDescription>
          </DialogHeader>
          {selectedRoom && (() => {
            const listed = selectedRoom.price;
            const floor = Math.max(1, Math.round(selectedRoom.min_price || listed * 0.7));
            const pctOff = Math.max(0, Math.round(((listed - Number(offerPrice)) / listed) * 100));
            return (
              <div className="space-y-5 mt-2">
                {/* Slider — bounded [floor, listed] */}
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-sm" data-testid="negotiation-slider-wrap">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="hw-label text-slate-500">Listed price</div>
                      <div className="hw-head text-lg font-black text-slate-500 line-through">₹{listed.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-center">
                      <div className="hw-label text-blue-700">Your offer</div>
                      <div className="hw-head text-3xl font-black text-emerald-700" data-testid="offer-price-display">
                        ₹{Number(offerPrice || 0).toLocaleString("en-IN")}
                      </div>
                      {pctOff > 0 && (
                        <div className="text-[11px] font-bold text-emerald-700">−{pctOff}% off</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="hw-label text-slate-500">Floor</div>
                      <div className="hw-head text-lg font-black text-slate-700" data-testid="floor-price-display">
                        ₹{floor.toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Slider
                      value={[Number(offerPrice) || floor]}
                      min={floor}
                      max={listed}
                      step={50}
                      onValueChange={(v) => { setOfferPrice(v[0]); setQuote(null); }}
                      className="mt-2"
                      data-testid="offer-price-slider"
                    />
                    <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                      <span>Lowest ₹{floor.toLocaleString("en-IN")}</span>
                      <span>Listed ₹{listed.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Label className="hw-label text-slate-500 text-xs">Fine-tune</Label>
                      <Input
                        type="number"
                        min={floor}
                        max={listed}
                        value={offerPrice}
                        onChange={(e) => {
                          const v = Number(e.target.value || floor);
                          setOfferPrice(Math.min(listed, Math.max(floor, v)));
                          setQuote(null);
                        }}
                        className="w-32 rounded-sm text-sm"
                        data-testid="offer-price-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="hw-label">Check-in</Label>
                    <Input type="date" min={new Date().toISOString().slice(0, 10)} value={checkin} onChange={(e) => { setCheckin(e.target.value); setQuote(null); }} className="rounded-sm mt-1" data-testid="checkin-input" />
                  </div>
                  <div>
                    <Label className="hw-label">Check-out</Label>
                    <Input type="date" min={checkin || new Date().toISOString().slice(0, 10)} value={checkout} onChange={(e) => { setCheckout(e.target.value); setQuote(null); }} className="rounded-sm mt-1" data-testid="checkout-input" />
                  </div>
                  <div>
                    <Label className="hw-label">Rooms</Label>
                    <Input type="number" min={1} value={roomsNeeded} onChange={(e) => { setRoomsNeeded(e.target.value); setQuote(null); }} className="rounded-sm mt-1" data-testid="rooms-input" />
                  </div>
                </div>

                {/* Quote result panel */}
                {quote && (
                  <div
                    className={`p-4 rounded-sm border ${
                      quote.decision === "accept"
                        ? "border-emerald-300 bg-emerald-50"
                        : quote.decision === "counter"
                          ? "border-amber-300 bg-amber-50"
                          : quote.decision === "reject"
                            ? "border-red-300 bg-red-50"
                            : quote.decision === "sold_out"
                              ? "border-red-400 bg-red-100"
                              : "border-blue-300 bg-blue-50"
                    }`}
                    data-testid="quote-result"
                  >
                    <div className="flex items-center gap-2">
                      {quote.decision === "accept" && <ThumbsUp className="w-5 h-5 text-emerald-700" />}
                      {quote.decision === "counter" && <Zap className="w-5 h-5 text-amber-700" />}
                      {quote.decision === "reject" && <AlertCircle className="w-5 h-5 text-red-700" />}
                      {quote.decision === "sold_out" && <Ban className="w-5 h-5 text-red-700" />}
                      {quote.decision === "manual_review" && <MessageSquare className="w-5 h-5 text-blue-700" />}
                      <div className="hw-head font-black text-slate-900 capitalize" data-testid="quote-decision">
                        {quote.decision === "accept" ? "AI accepted your offer!" :
                          quote.decision === "counter" ? "Best AI counter" :
                            quote.decision === "reject" ? "AI can't go that low" :
                              quote.decision === "sold_out" ? "Sold out for these dates" :
                                "Hotel prefers manual review"}
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="quote-message">{quote.message}</p>
                    {(quote.decision === "counter" || quote.decision === "reject") && (
                      <div className="mt-3 flex items-center justify-between bg-white/60 p-3 rounded-sm">
                        <div>
                          <div className="hw-label text-slate-500">Hotel counter</div>
                          <div className="hw-head text-2xl font-black text-slate-900" data-testid="quote-counter-price">
                            ₹{Number(quote.counter_price).toLocaleString("en-IN")}
                          </div>
                        </div>
                        <Button
                          disabled={booking}
                          onClick={() => acceptQuoteAndBook(quote.counter_price)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
                          data-testid="accept-counter-btn"
                        >
                          Book at ₹{Number(quote.counter_price).toLocaleString("en-IN")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setNegOpen(false); setQuote(null); }} className="rounded-sm">Cancel</Button>
            {quote?.decision === "accept" ? (
              <Button
                disabled={booking}
                onClick={() => acceptQuoteAndBook(offerPrice)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
                data-testid="book-at-offer-btn"
              >
                {booking ? "Locking in…" : `Book at ₹${Number(offerPrice).toLocaleString("en-IN")}`}
              </Button>
            ) : quote?.decision === "manual_review" ? (
              <Button
                onClick={submitNegotiation}
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm font-bold"
                data-testid="send-manual-offer-btn"
              >
                Send offer to hotel
              </Button>
            ) : (
              <Button
                disabled={quoting}
                onClick={getInstantQuote}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
                data-testid="submit-offer-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {quoting ? "Asking AI…" : "Get instant quote"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Dialog with price breakup + coupon */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent
          className="rounded-sm max-w-lg p-0 gap-0 max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col w-[calc(100vw-1rem)]"
          data-testid="book-dialog"
        >
          <DialogHeader className="p-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="hw-head font-black text-2xl">Confirm booking</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">{bookRoom?.room_type} at <span className="font-semibold">{hotel.name}</span></DialogDescription>
          </DialogHeader>

          {/* Iter31 — auto-slide hotel photo carousel */}
          {(hotel.photos || []).length > 0 && (
            <BookDialogCarousel photos={hotel.photos} />
          )}

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="book-body">
            {/* Dates summary + edit toggle */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="dates-summary">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-4 h-4 text-blue-700 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="hw-label text-slate-500">Your stay</div>
                    {checkin && checkout ? (
                      <div className="text-sm font-bold text-slate-900 mt-0.5 truncate" data-testid="dates-summary-text">
                        {checkin} → {checkout}
                      </div>
                    ) : (
                      <div className="text-sm text-amber-700 mt-0.5">Add check-in and check-out dates</div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditDates((v) => !v)}
                  className="text-xs font-bold text-blue-700 hover:text-blue-800 flex items-center gap-1 flex-shrink-0"
                  data-testid="edit-dates-toggle"
                >
                  <Pencil className="w-3 h-3" /> {editDates ? "Done" : "Edit"}
                </button>
              </div>
              {editDates && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="hw-label">Check-in</Label>
                    <Input type="date" min={new Date().toISOString().slice(0,10)} value={checkin} onChange={(e) => setCheckin(e.target.value)} className="rounded-sm mt-1" data-testid="book-checkin-input" />
                  </div>
                  <div>
                    <Label className="hw-label">Check-out</Label>
                    <Input type="date" min={checkin || new Date().toISOString().slice(0,10)} value={checkout} onChange={(e) => setCheckout(e.target.value)} className="rounded-sm mt-1" data-testid="book-checkout-input" />
                  </div>
                </div>
              )}
            </div>

            {/* Cancellation policy — customer sees BEFORE booking (Iter25) */}
            {bookRoom?.cancellation_policy && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3" data-testid="cancellation-policy-summary">
                <div className="hw-label text-emerald-800 flex items-center gap-1">
                  Cancellation · {bookRoom.cancellation_policy.replace('_', ' ')}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  {bookRoom.cancellation_policy === "flexible" && "100% refund up to 24 hrs before check-in."}
                  {bookRoom.cancellation_policy === "moderate" && "100% up to 48 hrs, 50% between 24-48 hrs, no refund after."}
                  {bookRoom.cancellation_policy === "strict" && "100% refund up to 7 days before check-in, no refund after."}
                  {bookRoom.cancellation_policy === "non_refundable" && "Non-refundable — you save more, but no refund on cancel."}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Refunds credited to your SwaanStay wallet within seconds.</div>
              </div>
            )}

            {/* Booking for: self / someone else + editable contact */}
            <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="booking-for-section">              <div>
                <div className="hw-label text-slate-500 mb-2">Booking for</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingFor("self")}
                    data-testid="booking-for-self"
                    className={`flex-1 rounded-sm border py-2 text-sm font-bold transition-colors ${bookingFor === "self" ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                  >Myself</button>
                  <button
                    type="button"
                    onClick={() => setBookingFor("other")}
                    data-testid="booking-for-other"
                    className={`flex-1 rounded-sm border py-2 text-sm font-bold transition-colors ${bookingFor === "other" ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                  >Someone else</button>
                </div>
              </div>

              {bookingFor === "other" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="guest-fields">
                  <div>
                    <Label className="hw-label">Guest name *</Label>
                    <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} className="rounded-sm mt-1" placeholder="Full name of check-in guest" data-testid="guest-name-input" />
                  </div>
                  <div>
                    <Label className="hw-label">Guest phone *</Label>
                    <Input type="tel" inputMode="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} className="rounded-sm mt-1" placeholder="+91 9XXXXXXXXX" data-testid="guest-phone-input" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="booker-contact-fields">
                <div>
                  <Label className="hw-label">Your email *</Label>
                  <Input type="email" value={bookerEmail} onChange={(e) => setBookerEmail(e.target.value)} className="rounded-sm mt-1" placeholder="you@example.com" data-testid="booker-email-input" />
                </div>
                <div>
                  <Label className="hw-label">Your phone *</Label>
                  <Input type="tel" inputMode="tel" value={bookerPhone} onChange={(e) => setBookerPhone(e.target.value)} className="rounded-sm mt-1" placeholder="+91 9XXXXXXXXX" data-testid="booker-phone-input" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">Invoice + booking confirmation will be sent to this email and WhatsApp&apos;d to this phone.</p>
            </div>

            {/* Coupon */}
            <div className="border border-slate-200 rounded-md p-3" data-testid="coupon-section">
              <div className="hw-label text-slate-500 flex items-center gap-2"><Tag className="w-3 h-3" /> Coupon</div>
              {couponApplied ? (
                <div className="mt-2 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="coupon-applied">
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-emerald-700" />
                    <span className="font-bold text-emerald-800">{couponApplied.code}</span>
                    <span className="text-xs text-emerald-700">− ₹{couponApplied.discount.toLocaleString("en-IN")}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setCouponApplied(null); setCouponCode(""); }}
                    className="text-slate-500 hover:text-slate-800"
                    data-testid="coupon-remove-btn"
                  ><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="mt-2 flex gap-2">
                    <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-sm uppercase" data-testid="coupon-input" />
                    <Button type="button" onClick={() => applyCoupon()} disabled={applying} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold" data-testid="coupon-apply-btn">
                      {applying ? "…" : "Apply"}
                    </Button>
                  </div>
                  {suggestedCoupon && suggestedCoupon.potential_discount > 0 && (
                    <button
                      type="button"
                      onClick={() => applyCoupon(suggestedCoupon.code)}
                      className="mt-2 text-xs text-blue-700 hover:text-blue-800 font-bold flex items-center gap-1"
                      data-testid="coupon-suggested-btn"
                    >
                      <Sparkles className="w-3 h-3" /> Best offer: <span className="underline">{suggestedCoupon.code}</span> — save ₹{suggestedCoupon.potential_discount.toLocaleString("en-IN")}
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Wallet apply (if traveller has balance) */}
            {walletBalance > 0 && (
              <div className="border border-slate-200 rounded-md p-3" data-testid="wallet-apply-section">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useWallet}
                    onChange={(e) => setUseWallet(e.target.checked)}
                    className="w-4 h-4 accent-blue-700"
                    data-testid="wallet-apply-checkbox"
                  />
                  <div className="flex-1 flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-emerald-700" />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Use SwaanStay Wallet</div>
                      <div className="text-xs text-slate-500">Available balance: ₹{walletBalance.toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                  {useWallet && priceBreakup?.walletUsed > 0 && (
                    <span className="text-xs font-bold text-emerald-700" data-testid="wallet-apply-amount">−₹{priceBreakup.walletUsed.toLocaleString("en-IN")}</span>
                  )}
                </label>
              </div>
            )}

            {/* Price breakup */}
            {priceBreakup && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="price-breakup">
                <div className="hw-label text-slate-500 mb-2">Price breakup</div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Room tariff</span><span className="font-semibold text-slate-900" data-testid="pb-base">₹{priceBreakup.base.toLocaleString("en-IN")}</span></div>
                  {priceBreakup.discount > 0 && (
                    <div className="flex justify-between text-emerald-700"><span>Coupon discount</span><span className="font-semibold" data-testid="pb-discount">− ₹{priceBreakup.discount.toLocaleString("en-IN")}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-600">GST (12%)</span><span className="font-semibold text-slate-900" data-testid="pb-taxes">₹{priceBreakup.taxes.toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Service fee</span><span className="font-semibold text-slate-900" data-testid="pb-service">₹{priceBreakup.service.toLocaleString("en-IN")}</span></div>
                  {priceBreakup.walletUsed > 0 && (
                    <div className="flex justify-between text-slate-700"><span>Wallet applied</span><span className="font-semibold" data-testid="pb-wallet">− ₹{priceBreakup.walletUsed.toLocaleString("en-IN")}</span></div>
                  )}
                  <div className="my-1 h-px bg-slate-200" />
                  <div className="flex justify-between text-base pt-1"><span className="hw-head font-black text-slate-900">Total payable</span><span className="hw-head font-black text-slate-900" data-testid="pb-total">₹{priceBreakup.total.toLocaleString("en-IN")}</span></div>
                </div>
              </div>
            )}

            {/* Payment method (Razorpay-style tabs) */}
            {priceBreakup && priceBreakup.total > 0 && (
              <div className="border border-slate-200 rounded-md" data-testid="payment-methods">
                <div className="p-3 pb-2 flex items-center justify-between">
                  <div className="hw-label text-slate-500 flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Payment method</div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Secured by Razorpay</span>
                </div>
                <div className="grid grid-cols-4 gap-1 border-t border-slate-100 bg-slate-50 rounded-b-md">
                  {[
                    { k: "upi", label: "UPI", icon: Smartphone },
                    { k: "card", label: "Cards", icon: CreditCard },
                    { k: "netbanking", label: "Netbanking", icon: Landmark },
                    { k: "wallet", label: "Wallets", icon: Layers },
                  ].map((m) => (
                    <button
                      key={m.k} type="button"
                      onClick={() => setPaymentMethod(m.k)}
                      className={`p-3 text-center transition-colors relative ${
                        paymentMethod === m.k ? "bg-white border-b-2 border-blue-700" : "hover:bg-white/60"
                      }`}
                      data-testid={`pay-${m.k}`}
                    >
                      <m.icon className={`w-4 h-4 mx-auto ${paymentMethod === m.k ? "text-blue-700" : "text-slate-600"}`} />
                      <div className={`text-[11px] mt-1 font-bold ${paymentMethod === m.k ? "text-blue-700" : "text-slate-700"}`}>{m.label}</div>
                    </button>
                  ))}
                </div>

                <div className="p-4 bg-emerald-50 border-t border-emerald-200 text-xs text-slate-700">
                  {paymentMethod === "upi" ? (
                    <>Continue to the secure Razorpay window — a <span className="font-bold">fresh UPI QR</span> is generated for every transaction. Scan with any UPI app.</>
                  ) : (
                    <>Continue to the secure Razorpay window to complete your payment.</>
                  )}
                </div>
              </div>
            )}

            {/* Fully paid by wallet */}
            {priceBreakup && priceBreakup.total === 0 && priceBreakup.walletUsed > 0 && (
              <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-md text-center" data-testid="wallet-full-notice">
                <Wallet className="w-6 h-6 text-emerald-700 mx-auto" />
                <div className="hw-head font-bold text-emerald-800 mt-1">Fully paid by wallet</div>
                <div className="text-xs text-emerald-700">No further payment required. Confirm to lock the booking.</div>
              </div>
            )}
          </div>

          {/* Sticky footer — always visible even on small screens */}
          <DialogFooter className="p-4 border-t border-slate-100 bg-white flex-shrink-0 flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setBookOpen(false)} className="rounded-sm flex-1 sm:flex-initial" data-testid="cancel-booking-btn">Cancel</Button>
            <Button onClick={submitBooking} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold flex-1 sm:flex-initial" data-testid="confirm-booking-btn">
              <Check className="w-4 h-4 mr-2" />
              {priceBreakup?.total > 0 ? `Pay ₹${priceBreakup?.total.toLocaleString("en-IN")}` : `Confirm ₹${priceBreakup?.total.toLocaleString("en-IN")}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-sm" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head font-black text-2xl">Share your experience</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">Rate <span className="font-semibold">{hotel.name}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="hw-label">Your rating</Label>
              <div className="mt-2"><StarRow value={rvRating} size="w-8 h-8" onSelect={setRvRating} /></div>
            </div>
            <div>
              <Label className="hw-label">Title (optional)</Label>
              <Input value={rvTitle} onChange={(e) => setRvTitle(e.target.value)} placeholder="Loved the rooftop!" className="rounded-sm mt-1" data-testid="review-title-input" />
            </div>
            <div>
              <Label className="hw-label">Comment</Label>
              <Textarea value={rvComment} onChange={(e) => setRvComment(e.target.value)} placeholder="Share the details of your stay…" className="rounded-sm mt-1" rows={5} data-testid="review-comment-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitReview} className="bg-blue-700 hover:bg-blue-800 text-white rounded-sm font-bold" data-testid="submit-review-btn">
              Submit review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter31 — AI Virtual Room Experience™ Modal */}
      <Dialog open={virtualRoomOpen} onOpenChange={setVirtualRoomOpen}>
        <DialogContent className="rounded-sm max-w-6xl p-0 gap-0 max-h-[95vh] overflow-hidden flex flex-col" data-testid="virtual-room-dialog">
          <DialogHeader className="p-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="hw-head font-black text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              AI Virtual Room Experience™
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Explore every corner of {hotel.name} before you book.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-900">
            {hotel.virtual_room?.status === "panorama" ? (
              <PanoramaViewer hotelIdentifier={hotel.slug || hotel.hotel_id} width={hotel.virtual_room.width} height={hotel.virtual_room.height} />
            ) : (
              <GuidedTour photos={hotel.virtual_room?.source_photos || hotel.photos || []} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------- Iter31: Panorama Viewer (drag-to-pan cylindrical panorama) ----------
function PanoramaViewer({ hotelIdentifier, width, height }) {
  const [dragX, setDragX] = useState(0);
  const [origin, setOrigin] = useState(null);
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setContainerW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute the display width so the panorama fills the container's height
  const displayHeight = 520;
  const displayWidth = width && height ? Math.round(width * (displayHeight / height)) : 0;
  const maxDrag = Math.max(0, displayWidth - containerW);
  const clampedX = Math.max(-maxDrag, Math.min(0, dragX));

  const onPointerDown = (e) => {
    setOrigin({ x: e.clientX, dragX: clampedX });
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!origin) return;
    setDragX(origin.dragX + (e.clientX - origin.x));
  };
  const onPointerUp = (e) => {
    setOrigin(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const API = process.env.REACT_APP_BACKEND_URL;
  const src = `${API}/api/public/hotels/${hotelIdentifier}/panorama`;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing select-none"
      style={{ height: displayHeight }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="panorama-viewer"
    >
      <img
        src={src}
        alt="Room panorama"
        draggable={false}
        className="absolute top-0 h-full max-w-none"
        style={{ transform: `translateX(${clampedX}px)`, width: displayWidth || "auto" }}
        data-testid="panorama-img"
      />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2 pointer-events-none">
        <ChevronLeft className="w-3 h-3" /> Drag to explore <ChevronRight className="w-3 h-3" />
      </div>
    </div>
  );
}

// Fallback: swipeable multi-photo guided tour
function GuidedTour({ photos }) {
  const [idx, setIdx] = useState(0);
  if (!photos.length) return <div className="grid place-items-center h-96 text-slate-400">No photos yet</div>;
  return (
    <div className="relative bg-slate-900" data-testid="guided-tour">
      <div className="relative w-full h-[520px] bg-black overflow-hidden">
        <img src={photos[idx]} alt={`View ${idx + 1}`} className="w-full h-full object-contain" data-testid="guided-tour-photo" />
        <button
          onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
          aria-label="Previous"
          data-testid="guided-tour-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => setIdx((i) => (i + 1) % photos.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
          aria-label="Next"
          data-testid="guided-tour-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
          {idx + 1} / {photos.length}
        </div>
      </div>
      <div className="p-4 bg-slate-900 flex gap-2 overflow-x-auto">
        {photos.map((p, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`flex-shrink-0 w-24 aspect-[4/3] rounded-sm overflow-hidden border-2 transition-all ${i === idx ? "border-emerald-400 scale-105" : "border-transparent opacity-70 hover:opacity-100"}`}
            data-testid={`guided-tour-thumb-${i}`}
          >
            <img src={p} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}


// ---------- Iter31: Book Dialog Carousel (auto-slide) ----------
function BookDialogCarousel({ photos }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (photos.length < 2 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 3200);
    return () => clearInterval(t);
  }, [photos.length, paused]);
  return (
    <div
      className="relative w-full aspect-[16/9] bg-slate-900 overflow-hidden"
      data-testid="book-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((p, i) => (
        <img
          key={i}
          src={p}
          alt={`Hotel photo ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0"}`}
          data-testid={`carousel-photo-${i}`}
        />
      ))}
      {photos.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="carousel-dots">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Photo ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-white w-6" : "bg-white/50 w-1.5"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
